// utils/numberHelper.js

/**
 * Formata um número decimal de acordo com as opções fornecidas.
 *
 * @param {number} number - O número a ser formatado.
 * @param {string} locale - O idioma e região para formatação (ex: 'pt-BR', 'en-US').
 * @param {Object} options - Opções de formatação para Intl.NumberFormat.
 * @returns {string} - O número formatado como string.
 */
export function formatDecimal(number, locale = 'pt-BR', options = {}) {
    if (isNaN(number)) return '';

    const defaultOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    };

    const formatOptions = { ...defaultOptions, ...options };

    try {
        return new Intl.NumberFormat(locale, formatOptions).format(number);
    } catch (error) {
        console.error('Erro ao formatar o número:', error);
        return number.toString(); // Retorna o número como string em caso de erro
    }
}

/**
 * Formata um valor como **inteiro** usando `Intl.NumberFormat`, respeitando `locale`
 * e opções passadas. Valores `NaN` retornam string vazia.
 *
 * - `minimumFractionDigits` e `maximumFractionDigits` são fixados em `0` por padrão
 *   (pode sobrescrever via `options`).
 * - Em caso de erro no `Intl.NumberFormat`, registra no console e retorna `number.toString()`.
 *
 * @param {number} number
 *        Valor a ser formatado. Se `NaN`, retorna `''`.
 * @param {string} [locale='pt-BR']
 *        Locale BCP 47 (ex.: `'pt-BR'`, `'en-US'`).
 * @param {Intl.NumberFormatOptions} [options={}]
 *        Opções adicionais para `Intl.NumberFormat`. Por padrão:
 *        `{ minimumFractionDigits: 0, maximumFractionDigits: 0 }`.
 * @returns {string}
 *        String com o número formatado, `''` se `NaN`, ou `number.toString()` em caso de erro.
 *
 * @example
 * // Padrão pt-BR (agrupamento por ponto)
 * formatInteger(1234567) // '1.234.567'
 *
 * @example
 * // Locale en-US
 * formatInteger(1234567, 'en-US') // '1,234,567'
 *
 * @example
 * // Sobrescrevendo opções (sem agrupamento)
 * formatInteger(1234567, 'pt-BR', { useGrouping: false }) // '1234567'
 *
 * @example
 * // Valor inválido
 * formatInteger(NaN) // ''
 */
export function formatInteger(number, locale = 'pt-BR', options = {}) {
    if (isNaN(number)) return '';

    const defaultOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    };

    const formatOptions = { ...defaultOptions, ...options };

    try {
        return new Intl.NumberFormat(locale, formatOptions).format(number);
    } catch (error) {
        console.error('Erro ao formatar o número:', error);
        return number.toString(); // Retorna o número como string em caso de erro
    }
}

/**
 * Formata um número com casas decimais e separadores de decimal e milhares
 * (comportamento semelhante ao PHP `number_format`).
 *
 * Regras:
 * - Remove caracteres não numéricos (exceto sinais +/-, notação exponencial E/e e ponto).
 * - Usa arredondamento estável com notação exponencial para evitar erros de precisão
 *   (ex.: 1.005 com 2 casas → 1.01).
 * - Valores não finitos resultam em 0 (exceto `null`/`undefined`, que retornam `undefined`).
 *
 * @param {number|string|null|undefined} number
 *        Valor a ser formatado. Se `null`/`undefined`, a função retorna `undefined`.
 *        Strings numéricas são aceitas.
 * @param {number} [decimals=0]
 *        Quantidade de casas decimais (≥ 0). Valores não numéricos resultam em 0.
 * @param {string} [decPoint='.']
 *        Separador decimal.
 * @param {string} [thousandsSep=',']
 *        Separador de milhares.
 * @returns {string|undefined}
 *        O número formatado como string ou `undefined` se `number` for `null`/`undefined`.
 *
 * @example
 * // Padrão en-US
 * numberFormat(1234.5) // '1,235'
 *
 * @example
 * // Duas casas, decimal vírgula e milhar ponto (pt-BR)
 * numberFormat(1234.5, 2, ',', '.') // '1.234,50'
 *
 * @example
 * // Entrada como string e 3 casas
 * numberFormat('12 345.6789', 3, ',', '.') // '12.345,679'
 *
 * @example
 * // Precisão em casos problemáticos
 * numberFormat(1.005, 2) // '1.01'
 */
export function numberFormat(number, decimals, decPoint, thousandsSep) {
    if(number == null) {
        return
    }

    number = (number + '').replace(/[^0-9+\-Ee.]/g, '')
    const n = !isFinite(+number) ? 0 : +number
    const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals)
    const sep = typeof thousandsSep === 'undefined' ? ',' : thousandsSep
    const dec = typeof decPoint === 'undefined' ? '.' : decPoint
    let s

    const toFixedFix = function (n, prec) {
        if (('' + n).indexOf('e') === -1) {
            return +(Math.round(n + 'e+' + prec) + 'e-' + prec)
        } else {
            const arr = ('' + n).split('e')
            let sig = ''
            if (+arr[1] + prec > 0) {
                sig = '+'
            }
            return (+(Math.round(+arr[0] + 'e' + sig + (+arr[1] + prec)) + 'e-' + prec)).toFixed(prec)
        }
    }

    // @todo: for IE parseFloat(0.55).toFixed(0) = 0;
    s = (prec ? toFixedFix(n, prec).toString() : '' + Math.round(n)).split('.')
    if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
    }
    if ((s[1] || '').length < prec) {
        s[1] = s[1] || ''
        s[1] += new Array(prec - s[1].length + 1).join('0')
    }

    return s.join(dec)
}

/**
 * Converte uma string numérica formatada em um número, tratando casas decimais.
 *
 * @param {string|number} value - O valor a ser convertido. Pode ser uma string formatada (ex: "1.234,56") ou um número
 * @param {number} [decimals] - Número de casas decimais a serem consideradas na conversão
 * @returns {number|null} Retorna o número convertido ou null se o valor de entrada for null/undefined
 *
 * @example
 * // Retorna 1234.56
 * stringToNumber("1.234,56", 2)
 *
 * @example
 * // Retorna 1234
 * stringToNumber("1.234")
 *
 * @example
 * // Retorna null
 * stringToNumber(null)
 */
export function stringToNumber(value, decimals){
    if(value == null || value === undefined) {
        return null
    }

    let valueRaw

    if (typeof value != 'number') {
        //console.log('valueRaw Inicial: ', valueRaw )
        valueRaw = value.replaceAll('.', '').replaceAll(',', '')

        if(decimals && decimals > 0) {
            let numberParts = [];

            numberParts.push(valueRaw.slice(0, -decimals))
            numberParts.push(valueRaw.slice(-decimals))

            valueRaw = numberParts.join('.')

            //console.log('valueRaw', valueRaw)

            valueRaw = parseFloat(valueRaw)
            valueRaw = parseFloat(valueRaw.toFixed(decimals));

            value = valueRaw
        }
    }

    return value
}

/**
 * Converte um valor monetário (número ou string formatada pt-BR/en-US) em `number`.
 *
 * - Números são retornados como estão.
 * - Strings com vírgula são tratadas como pt-BR (`.` milhar, `,` decimal), ex.: `"1.234,56"` → `1234.56`.
 * - Strings sem vírgula são tratadas como padrão (`.` decimal), ex.: `"1234.56"` → `1234.56`.
 * - `null`/`undefined`/valores inválidos retornam `0`.
 *
 * @param {number|string|null|undefined} value
 * @returns {number}
 *
 * @example
 * parseMoney("1.234,56") // 1234.56
 * parseMoney("1234.56") // 1234.56
 * parseMoney(null) // 0
 */
export function parseMoney(value) {
    if (typeof value === 'number') return value;
    if (!value) return 0;

    if (typeof value === 'string') {
        if (value.includes(',')) {
            const clean = value.replaceAll('.', '').replace(',', '.');
            return Number.parseFloat(clean) || 0;
        }
        return Number.parseFloat(value) || 0;
    }

    return 0;
}

/**
 * Arredonda um número para a quantidade de casas decimais informada.
 *
 * @param {number} value
 * @param {number} decimals
 * @returns {number} `0` se `value` não for um número finito.
 *
 * @example
 * roundDecimal(1.005, 2) // 1.01
 * roundDecimal(-1.005, 2) // -1.01
 */
export function roundDecimal(value, decimals) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    const factor = Math.pow(10, decimals);
    const sign = Math.sign(n) || 1;
    return (sign * Math.round(Math.abs(n) * factor)) / factor;
}

/**
 * Transform do Yup para campos monetários: converte `originalValue` (string) via {@link parseMoney}
 * antes da validação, preservando `value` quando a entrada já não é string.
 *
 * @param {number} value
 * @param {number|string} originalValue
 * @returns {number}
 *
 * @example
 * Yup.number().transform(transformMoney).required()
 */
export function transformMoney(value, originalValue) {
    if (typeof originalValue === 'string') {
        return parseMoney(originalValue);
    }
    return value;
}

/**
 * Formata um número como moeda, adicionando o símbolo correspondente (ex: R$).
 *
 * @param {number} number - O valor a ser formatado. Se `NaN`, retorna `''`.
 * @param {string} [locale='pt-BR'] - O idioma e região para formatação (ex: 'pt-BR', 'en-US').
 * @param {string} [currency='BRL'] - O código da moeda (ISO 4217, ex: 'BRL', 'USD', 'EUR').
 * @param {Intl.NumberFormatOptions} [options={}] - Opções adicionais para Intl.NumberFormat.
 * @returns {string} - O número formatado como moeda, ou o número como string em caso de erro.
 *
 * @example
 * // Retorna "R$ 1.234,50" (o espaçamento exato depende do motor JS)
 * formatCurrency(1234.5)
 *
 * @example
 * // Retorna "$1,234.50"
 * formatCurrency(1234.5, 'en-US', 'USD')
 *
 * @example
 * // Sobrescrevendo opções (sem casas decimais)
 * formatCurrency(1234.5, 'pt-BR', 'BRL', { maximumFractionDigits: 0 }) // "R$ 1.235"
 */
export function formatCurrency(number, locale = 'pt-BR', currency = 'BRL', options = {}) {
    if (Number.isNaN(number)) return '';

    const formatOptions = {
        style: 'currency',
        currency: currency,
        ...options,
    };

    try {
        return new Intl.NumberFormat(locale, formatOptions).format(number);
    } catch (error) {
        console.error('Erro ao formatar como moeda:', error);
        return number.toString(); // Retorna o número como string em caso de erro
    }
}
